/**
 * EnumValuesDetailsDialog — sub-dialog opened from the MetaInfo enum-columns
 * list ("Details" button) and from column-header glyph clicks on declared
 * enum columns. Shows the cached values, the source (native / check / sampled),
 * truncation state, and a Refresh-from-data action. The user can also remove
 * the declaration.
 *
 * **Implicit variant** (`implicit`): the column has NO declaration of its own —
 * it behaves as a lookup enum only because its FK target's table carries a
 * `ReferenceTableDeclaration`. Everything above still applies (pairs, badge,
 * provenance, refresh, label column), but "Remove declaration" would have
 * nothing to remove, so the actions row offers the two things that CAN be done
 * from here: declare this one column explicitly (so it survives the flag), or
 * stop treating the target table as a reference table (which affects every
 * column referencing it — see `referenceTableActions`).
 */

import { useEffect, useState } from 'react';
import { Link2Off, Pin, RefreshCw, Trash2, X } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type {
  EnumLookupTarget,
  EnumValueCache,
  EnumValueSource,
  MetaInfoProvenance,
} from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { addEnumColumn, fetchEnumValues } from '../../services/metaInfoApi';
import { useInvalidateMetaInfo, useMetaInfo } from '../../hooks/useMetaInfo';
import { referenceTableModeFor } from '../../utils/effectiveLookupDeclaration';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { DocsLinkButton } from '@/features/help-feedback';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { classifyEnumValueError } from '../../utils/enumValueErrorClassifier';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';
import { OperationErrorAlert } from '../feedback/OperationErrorAlert';
import { useLookupEnumLabelColumn } from '../filters/useLookupEnumLabelColumn';
import type { TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';
import { LooseFkDisplayColumnSelect } from '../filters/LooseFkDisplayColumnSelect';
import { confirmRemoveReferenceTable } from './referenceTableActions';
import { ProvenanceBadge } from './ProvenanceBadge';

const sourceLabel = (source: EnumValueSource): string => {
  if (source === 'native') return 'Native enum type';
  if (source === 'check-constraint') return 'CHECK constraint';
  if (source === 'lookup') return 'Values from a referenced table';
  return 'Sampled from data';
};

/** Short badge text — mirrors `EnumColumnsSection`'s `sourceBadge`. */
const sourceBadge = (source: EnumValueSource): string => {
  if (source === 'native') return 'native';
  if (source === 'check-constraint') return 'check';
  if (source === 'lookup') return 'lookup';
  return 'sampled';
};

export type EnumValuesDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  table: string;
  column: string;
  /** Cached values for this declaration (may be undefined when not yet sampled). */
  cache: EnumValueCache | undefined;
  /** Declared source — used as the default refresh source. */
  declaredSource: EnumValueSource;
  /** Refetch context. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  /** When provided, a "Remove declaration" footer button is rendered. */
  onRemoveDeclaration?: () => void;
  /** Path-scoped enum declaration — e.g. `$.status`. Drives copy + scopes
   * the refresh so sampling reads the path, not the host column. */
  path?: string;
  /** Present iff `path` is set. */
  pathKind?: 'scalar' | 'array';
  /** Present iff `declaredSource === 'lookup'` — the FK target the cached
   * key→label pairs are read from. The label column itself is NOT stored
   * here; it's resolved live via `useLookupEnumLabelColumn`. */
  lookup?: EnumLookupTarget;
  /**
   * True when this column has NO `EnumColumnDeclaration` of its own and is a
   * lookup enum only because `lookup`'s table is flagged as a reference table.
   * Swaps "Remove declaration" for the two actions that apply there.
   */
  implicit?: boolean;
  /** The declaration's provenance flag, badged next to the source badge.
   *  Absent = user-authored (the normal case) — see `MetaInfoProvenance`.
   *  Never set for an `implicit` column: there is no declaration to flag. */
  provenance?: MetaInfoProvenance;
  /** Host metadata provider (Azure Table, S4 review D, D5) — the lookup label column is read through it. */
  metadataEditors?: TableExplorerMetadataEditorProviders;
};

export function EnumValuesDetailsDialog({
  open,
  onOpenChange,
  schema,
  table,
  column,
  cache,
  declaredSource,
  connectionId,
  database,
  engine,
  onRemoveDeclaration,
  path,
  pathKind,
  lookup,
  implicit,
  provenance,
  metadataEditors,
}: EnumValuesDetailsDialogProps) {
  const isPath = !!path;
  // Only the flag's MODE is read from here — which of the two promises the
  // user is dropping (`feature:enum_reference_table_undeclare { mode }`).
  const { data: metaInfo } = useMetaInfo(connectionId ?? null, database ?? null);
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeclaring, setIsDeclaring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped whenever the user picks a different label column so an effect can
  // persist-if-touched + refresh AFTER the hook's state has settled (the
  // `onChange` call below only queues a state update — reading the hook's
  // return value synchronously in the same handler would see the stale,
  // pre-pick closure).
  const [labelChangeTick, setLabelChangeTick] = useState(0);

  const source = cache?.source ?? declaredSource;
  const isLookup = source === 'lookup';

  const labelColumnState = useLookupEnumLabelColumn({
    connectionId: connectionId ?? null,
    database: database ?? null,
    target: isLookup ? lookup : undefined,
    ...(metadataEditors ? { metadataEditors } : {}),
  });
  const [refusedReason, setRefusedReason] = useState<string | null>(null);

  // The implicit variant needs a target to name and act on: without one there
  // is no reference table to drop and nothing to copy onto a declaration.
  const isImplicit = !!implicit && isLookup && !!lookup;

  // Reset transient state whenever the dialog reopens.
  useEffect(() => {
    if (open) {
      setError(null);
      setRefusedReason(null);
      setIsRefreshing(false);
      setIsDeclaring(false);
    }
  }, [open]);

  const canRefresh = Boolean(connectionId && database && engine);
  const values = cache?.values ?? [];
  const truncated = cache?.truncated ?? false;
  // A lookup declaration whose `lookup` target is somehow missing can't be
  // refreshed — defensive; shouldn't happen since `lookup` is always set
  // alongside `source: 'lookup'`.
  const lookupTargetMissing = isLookup && !lookup;

  const handleRefresh = async () => {
    if (!connectionId || !database || !engine) return;
    if (lookupTargetMissing) return;
    // S4 review D, D5: a lookup refresh with no label column for lack of information (the target's columns are
    // still loading or could not be read) would replace the named pairs with keys-only ones. A host (Azure Table)
    // refuses it; SQL keeps its behaviour under the S4 slice's byte-for-byte rule (the same failure mode is recorded
    // for its owner in the S4 NOTES, review round E).
    const labelsAtRisk = isLookup && labelColumnState.labelColumn === null && labelColumnState.selectValue !== '__none__'
      && (!labelColumnState.ready || labelColumnState.columnsUnavailable);
    if (labelsAtRisk && (metadataEditors || engine === 'azurestorage') && lookup) {
      setRefusedReason(`Could not read the columns of ${qualifyLookupTarget(lookup.toSchema, lookup.toTable)}, so its labels cannot be refreshed here; nothing was changed.`);
      return;
    }
    setRefusedReason(null);
    setIsRefreshing(true);
    setError(null);
    try {
      const result = await fetchEnumValues(
        connectionId,
        database,
        schema,
        table,
        column,
        engine,
        declaredSource,
        path,
        pathKind,
        isLookup && lookup
          ? {
              toSchema: lookup.toSchema,
              toTable: lookup.toTable,
              toColumn: lookup.toColumn,
              labelColumn: labelColumnState.labelColumn,
            }
          : undefined,
      );
      track('feature:enum_values_refresh', {
        engine,
        source: result.source,
        on_json_path: isPath,
        // Distinguishes the user pressing Refresh from the background
        // refreshes (`miss` — `useLookupLabelSelfHeal`).
        reason: 'manual',
      });
      await invalidate(connectionId, database);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * "Declare on this column only" — copy the implicit resolution onto a real
   * `EnumColumnDeclaration` so this column keeps its names even if the
   * reference-table flag is dropped later. The pairs already cached under this
   * column's identity are exactly the ones the declaration will read, so no
   * fetch is needed: the dialog simply re-renders in its explicit state.
   */
  const handleDeclareExplicit = async () => {
    if (!connectionId || !database || !engine || !lookup) return;
    setIsDeclaring(true);
    setError(null);
    try {
      await addEnumColumn(connectionId, database, {
        id: crypto.randomUUID(),
        schema,
        table,
        column,
        source: 'lookup',
        createdAt: new Date().toISOString(),
        lookup,
      });
      track('feature:enum_declare', {
        engine,
        source: 'lookup',
        trigger: 'implicit-details',
      });
      await invalidate(connectionId, database);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Declaring the column failed');
    } finally {
      setIsDeclaring(false);
    }
  };

  /** "Stop using as reference table" (on <target>) — affects EVERY column. */
  const handleRemoveReferenceTable = () => {
    if (!connectionId || !database || !lookup) return;
    confirmRemoveReferenceTable({
      connectionId,
      database,
      schema: lookup.toSchema,
      table: lookup.toTable,
      engine,
      mode: referenceTableModeFor(metaInfo, lookup),
      trigger: 'details-dialog',
      track,
      invalidate,
      onRemoved: () => onOpenChange(false),
    });
  };

  const handleLabelColumnChange = (next: string) => {
    labelColumnState.onChange(next);
    setLabelChangeTick((t) => t + 1);
  };

  // Persist the shared `tableDisplayColumns` entry (only when the user
  // actually touched the dropdown — the hook enforces that) and re-fetch the
  // pairs so they reflect the newly-picked label column.
  useEffect(() => {
    if (labelChangeTick === 0) return;
    let cancelled = false;
    (async () => {
      await labelColumnState.persistIfTouched();
      if (cancelled) return;
      await handleRefresh();
    })();
    return () => {
      cancelled = true;
    };
    // Only re-run when the user changes the dropdown — `labelColumnState` and
    // `handleRefresh` are re-created every render; including them would
    // re-fire this on every keystroke elsewhere in the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelChangeTick]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="metainfo-enum-details-dialog"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          aria-label="Close"
          data-testid="enum-details-close-x"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>
              {isPath ? 'JSON-path enum values' : 'Enum values'}
            </DialogTitle>
            <DocsLinkButton
              url={
                isPath
                  ? 'https://jamsql.com/docs/json-columns/'
                  : 'https://jamsql.com/docs/enum-columns/'
              }
              doc={isPath ? 'json-columns' : 'enum-columns'}
            />
          </div>
          <DialogDescription>
            <span
              translate="no"
              className="font-mono text-xs"
              data-testid="enum-details-target"
            >
              {schema}.{table}.{column}
              {path ? ` · ${path}` : ''}
            </span>
          </DialogDescription>
          <p
            className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground"
            data-testid="enum-details-source"
          >
            <span
              className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
              data-testid="enum-details-source-badge"
            >
              {sourceBadge(source)}
            </span>
            <ProvenanceBadge provenance={provenance} />
            {/* An implicit column's provenance is ONE fact — it comes from a
                reference table, that table has a label column, and the values
                were read at some point. Three stacked lines (source · target ·
                "implicit — via …") read as three competing claims about where
                the values come from, and the qualified table name appeared in
                two of them. Collapsed into one line, in the source slot, so
                the badge still leads it. */}
            {isImplicit && lookup ? (
              <span data-testid="enum-details-implicit-note">
                implicit — via reference table{' '}
                <span className="font-mono" translate="no">
                  {qualifyLookupTarget(lookup.toSchema, lookup.toTable)}
                </span>{' '}
                · label:{' '}
                <span className="font-mono" translate="no">
                  {labelColumnState.labelColumn ?? '(none)'}
                </span>
                {cache?.sampledAt ? ` · scanned ${formatRelativeTime(cache.sampledAt)}` : ''}
              </span>
            ) : (
              <span>
                {sourceLabel(source)}
                {isPath
                  ? pathKind === 'array'
                    ? ' (JSON array elements)'
                    : ' (JSON property)'
                  : ''}
                {cache?.sampledAt
                  ? ` · scanned ${formatRelativeTime(cache.sampledAt)}`
                  : ''}
              </span>
            )}
          </p>
          {/* Only the collapsed line above replaces this one — an implicit
              column that has LOST its target still owes the user the
              "refresh is unavailable" explanation below. */}
          {isLookup &&
            !(isImplicit && lookup) &&
            (lookup ? (
              <p
                className="mt-1 text-[11px] text-muted-foreground"
                data-testid="enum-details-lookup-provenance"
              >
                Values from{' '}
                <span className="font-mono" translate="no">
                  {qualifyLookupTarget(lookup.toSchema, lookup.toTable)}
                </span>{' '}
                · label:{' '}
                <span className="font-mono" translate="no">
                  {labelColumnState.labelColumn ?? '(none)'}
                </span>
              </p>
            ) : (
              <p
                className="mt-1 text-[11px] italic text-muted-foreground"
                data-testid="enum-details-lookup-missing-target"
              >
                Lookup target is missing — refresh is unavailable.
              </p>
            ))}
        </DialogHeader>

        {truncated && (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs"
            data-testid="enum-details-truncated"
          >
            {isLookup
              ? 'More than 200 rows — use the row picker (lookup filter) for large reference tables.'
              : isPath
                ? 'More than 200 distinct values — this JSON path probably isn’t an enum. Consider undeclaring it and using a free-text filter.'
                : 'More than 200 distinct values — this column probably isn’t an enum. Consider undeclaring it and using a free-text filter.'}
          </div>
        )}

        <div
          className="flex flex-wrap gap-1 max-h-[300px] overflow-auto"
          data-testid="enum-details-values"
        >
          {values.length === 0 ? (
            <span className="text-xs text-muted-foreground italic">
              No values cached yet.
            </span>
          ) : (
            values.map((v, i) => {
              // Lookup declarations render `key → label` pairs when the cache
              // has labels; a NULL label falls back to the bare-key chip
              // (same rendering as every other source).
              const label = isLookup ? (cache?.labels?.[i] ?? null) : null;
              return (
                <span
                  key={v}
                  translate="no"
                  className="inline-flex items-center rounded border bg-muted/50 px-1.5 py-0.5 text-[11px] font-mono"
                  data-testid={label === null ? 'enum-details-value' : 'enum-details-value-pair'}
                >
                  {v}
                  {label === null ? null : (
                    <>
                      <span className="text-muted-foreground"> → </span>
                      {label}
                    </>
                  )}
                </span>
              );
            })
          )}
        </div>

        {isLookup && lookup && (
          <LooseFkDisplayColumnSelect
            value={labelColumnState.selectValue}
            onChange={handleLabelColumnChange}
            columnOptions={labelColumnState.columnOptions}
            loading={labelColumnState.loading}
            targetSchema={lookup.toSchema}
            targetTable={lookup.toTable}
          />
        )}

        {error &&
          (() => {
            // The dialog's only network action is `handleRefresh`, which calls
            // `fetchEnumValues` — so any error here is a sample failure. The
            // classifier picks JSON-property / JSON-array / column wording.
            const classified = classifyEnumValueError(error, 'sample', {
              isPath,
              pathKind,
            });
            return (
              <OperationErrorAlert
                testId="enum-details-error"
                title={classified.title}
                description={classified.description}
                details={classified.details}
              />
            );
          })()}

        {/* flex-wrap: the implicit variant carries four buttons — without it the
            row overflows the dialog and clips the Close button. */}
        <DialogFooter className="gap-2 flex-wrap">
          {isImplicit && lookup ? (
            // Nothing was declared on this column, so "Remove declaration"
            // would be a no-op. These two are what the user can actually do.
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeclareExplicit}
                disabled={isDeclaring || !connectionId || !database || !engine}
                data-testid="enum-details-declare-explicit"
              >
                <Pin className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Declare on this column only
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveReferenceTable}
                disabled={!connectionId || !database}
                className="text-destructive hover:text-destructive"
                data-testid="enum-details-remove-reference-table"
              >
                <Link2Off className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                {/* The implicit note + provenance line above already name the
                    table, and the confirm spells out the full consequence —
                    the qualified name here made the footer overflow. */}
                Stop using as reference table
              </Button>
            </>
          ) : (
            onRemoveDeclaration && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRemoveDeclaration}
                className="text-destructive hover:text-destructive"
                data-testid="enum-details-remove-declaration"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Remove declaration
              </Button>
            )
          )}
          {canRefresh && (
            <div className="flex flex-col items-end gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing || lookupTargetMissing}
                data-testid="enum-details-refresh"
              >
                {isRefreshing ? (
                  <RandomDotmSpinner size={14} className="mr-2" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                )}
                Refresh from data
              </Button>
              {lookupTargetMissing && (
                <span
                  className="text-[10px] text-muted-foreground italic"
                  data-testid="enum-details-refresh-disabled-note"
                >
                  Lookup target is missing.
                </span>
              )}
              {refusedReason && (
                <span role="alert" className="max-w-64 text-right text-[10px] text-destructive" data-testid="enum-details-refresh-refused">
                  {refusedReason}
                </span>
              )}
            </div>
          )}
          <Button
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="enum-details-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
